<script setup lang="ts">
    import InputLabel from '../InputLabel.vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import PanelTabs from '../PanelTabs.vue';
    import PanelTab from '../PanelTab.vue';
    import OrgDataAccessControl from './OrgDataAccessControl.vue';
    import ModuleAccessControl from './ModuleAccessControl.vue';
    import PermissionsSummary from '../users/PermissionsSummary.vue';
    import { __ } from '@/js/stores/expressions';
    import { ref, watch, computed, onMounted } from 'vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import type {
        ResponseGetRole,
        AccessLevel,
        AccessGroupNode,
        ApiPermission,
    } from '@/js/services/api/role/types';
    import { RoleOrgAccessLevel } from '@/js/services/api/role/types';
    import { useQueryGetRolePermissions } from '@/js/services/api/role/roleQueries';

    const props = defineProps<{
        role?: ResponseGetRole | null;
        roleId?: string;
    }>();

    const emit = defineEmits(['save']);

    const form = ref({
        name: '',
        roleDataAccess: false,
    });

    const accessLevel = ref<AccessLevel>(undefined);
    const accessGroups = ref<AccessGroupNode[]>([]);
    // The role's saved tree. Selecting Custom resets the editor to this.
    const orgBaseline = ref<AccessGroupNode[]>([]);
    const rolePermissions = ref<Record<string, number>>({});

    const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

    const { data: permissionsData } = useQueryGetRolePermissions(props.roleId ?? '');

    const canSubmit = computed(() => !!form.value.name.trim());

    watch(
        () => props.role?.id,
        () => {
            if (!props.role) return;

            form.value = {
                name: props.role.name,
                roleDataAccess: props.role.ownTeamAgg,
            };
            accessLevel.value = props.role.roleOrgAccessType;
            accessGroups.value = props.role.orgDataAccessTree;
            orgBaseline.value = deepClone(props.role.orgDataAccessTree);
        },
        { immediate: true }
    );

    const initialRolePermissions = ref<Record<string, number>>({});

    watch(
        () => permissionsData.value?.rolePermissions,
        (newPermissions) => {
            if (newPermissions) {
                rolePermissions.value = { ...newPermissions };
                initialRolePermissions.value = { ...newPermissions };
            }
        },
        { immediate: true }
    );

    const hasPermissionsChanged = () => {
        const current = rolePermissions.value;
        const initial = initialRolePermissions.value;
        const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
        return [...keys].some((key) => current[key] !== initial[key]);
    };

    // Strip unknown bits from backend — the saved value must equal the sum of
    // checked flags, so old/undefined bits can't linger in the payload.
    const sanitizeRolePermissions = (
        perms: ApiPermission[],
        rolePerms: Record<string, number>
    ): Record<string, number> =>
        Object.fromEntries(
            perms.map((p) => {
                const knownMask = Object.keys(p.valueDefinitions)
                    .map(Number)
                    .filter((n) => n !== 0)
                    .reduce((sum, flag) => sum | flag, 0);
                return [p.name, (rolePerms[p.name] ?? 0) & knownMask];
            })
        );

    const submit = () => {
        if (!canSubmit.value) return;
        emit('save', {
            name: form.value.name,
            roleOrgAccessType: accessLevel.value ?? RoleOrgAccessLevel.Custom,
            ownTeamAgg: form.value.roleDataAccess,
            orgDataAccessTree: accessGroups.value,
            rolePermissions: permissionsData.value
                ? sanitizeRolePermissions(permissionsData.value.permissions, rolePermissions.value)
                : rolePermissions.value,
            permissionsChanged: hasPermissionsChanged(),
        });
    };

    const { setFocus } = useElementFocus();

    onMounted(() => {
        if (!props.role) {
            setFocus('#roleName');
        }
    });

    defineExpose({
        submit,
        canSubmit,
    });
</script>

<template>
    <form class="my-10" @submit.prevent="submit">
        <div class="max-w-[720px]">
            <InputLabel for="roleName">
                {{ __('role-name') }}
            </InputLabel>
            <TextInput id="roleName" v-model="form.name" :width="TextInputWidth.Large" />
        </div>

        <template v-if="role">
            <h2 class="mt-10 heading-md">{{ __('role-permissions-heading') }}</h2>

            <div class="mt-4">
                <PanelTabs>
                    <!-- Summary: read-only roll-up of the role's effective
                         access. No baseline to compare against, so no badges. -->
                    <PanelTab :label="__('user-summary-tab')" default>
                        <PermissionsSummary
                            v-if="permissionsData?.permissions"
                            :permissions="permissionsData.permissions"
                            :effective-permissions="rolePermissions"
                            :groups="accessGroups"
                            :access-level="accessLevel"
                        />
                    </PanelTab>

                    <PanelTab :label="__('role-tab-data')">
                        <OrgDataAccessControl
                            v-model:access-level="accessLevel"
                            v-model:groups="accessGroups"
                            v-model:agent-groups="form.roleDataAccess"
                            :custom-baseline="orgBaseline"
                            show-agent-groups
                        />
                    </PanelTab>

                    <PanelTab :label="__('role-tab-functionality')">
                        <ModuleAccessControl
                            v-if="permissionsData?.permissions"
                            v-model:role-permissions="rolePermissions"
                            :permissions="permissionsData.permissions"
                        />
                    </PanelTab>
                </PanelTabs>
            </div>
        </template>
    </form>
</template>
